import { z } from 'zod';
/**
 * The starter catalog of acting sites. Agencies seed their "asks" from a subset of these
 * (`DEFAULT_CREDENTIAL_SITE_KEYS`); the talent's All-agency vault offers all of them as
 * one-tap "add" suggestions so they can visually see where to go.
 */
export const CREDENTIAL_SITES = [
    { key: 'actors_access', label: 'Actors Access', url: 'https://www.actorsaccess.com', hint: 'Breakdown Services — submissions & sides.' },
    { key: 'casting_networks', label: 'Casting Networks', url: 'https://www.castingnetworks.com', hint: 'Profiles, submissions & self-tapes.' },
    { key: 'backstage', label: 'Backstage', url: 'https://www.backstage.com', hint: 'Casting calls & auditions.' },
    { key: 'casting_frontier', label: 'Casting Frontier', url: 'https://www.castingfrontier.com', hint: 'Commercial casting & check-in.' },
    { key: 'imdbpro', label: 'IMDbPro', url: 'https://pro.imdb.com', hint: 'Industry profile & credits.' },
    { key: 'eco_cast', label: 'Eco Cast', url: 'https://www.castingnetworks.com', hint: 'Casting Networks self-tape requests.' },
    { key: 'cast_it', label: 'Cast It Talent', url: 'https://www.castittalent.com', hint: 'Studio casting submissions.' },
    { key: 'now_casting', label: 'Now Casting', url: 'https://www.nowcasting.com', hint: 'Casting & talent tools.' },
    { key: 'la_casting', label: 'LA Casting', url: 'https://www.lacasting.com', hint: 'Casting Networks (LA market).' },
    { key: 'central_casting', label: 'Central Casting', url: 'https://www.centralcasting.com', hint: 'Background / extras casting.' },
];
/** The sites a brand-new agency asks for out of the box. */
export const DEFAULT_CREDENTIAL_SITE_KEYS = [
    'actors_access',
    'casting_networks',
    'backstage',
    'casting_frontier',
    'imdbpro',
];
const SITE_BY_KEY = new Map(CREDENTIAL_SITES.map((s) => [s.key, s]));
/** A custom slug is allowed for sites the catalog doesn't know about. */
const SITE_KEY_RE = /^[a-z0-9_]{1,48}$/;
/**
 * A site an AGENCY defined itself (beyond the built-in catalog) to ask its talent for — same
 * shape as a catalog `CredentialSite`, stored on `businesses.custom_credential_sites`. The key
 * is a slug that must NOT collide with a built-in one, so a business catalog is unambiguous.
 */
export const customCredentialSiteSchema = z.object({
    key: z.string().regex(SITE_KEY_RE).refine((k) => !SITE_BY_KEY.has(k), 'key collides with a built-in site'),
    label: z.string().trim().min(1).max(120),
    url: z.string().trim().max(500).optional().nullable(),
    hint: z.string().trim().max(200).optional().nullable(),
});
export const customCredentialSitesSchema = z.array(customCredentialSiteSchema).max(40);
/** Normalize a business's stored custom sites (null/invalid → none) into full `CredentialSite`s. */
export function customCredentialSitesOf(stored) {
    const parsed = customCredentialSitesSchema.safeParse(stored);
    if (!parsed.success)
        return [];
    return parsed.data.map((c) => ({ key: c.key, label: c.label, url: c.url ?? '', hint: c.hint ?? undefined }));
}
/**
 * The full catalog a given business asks against = the built-in sites plus its own custom ones.
 * Custom keys can never shadow a built-in (schema-enforced), so ordering is stable: built-ins first.
 */
export function credentialCatalogOf(customStored) {
    return [...CREDENTIAL_SITES, ...customCredentialSitesOf(customStored)];
}
/** Look up a site by key in the given catalog (defaults to the built-in one); undefined if unknown. */
export function credentialSiteByKey(key, catalog) {
    if (!key)
        return undefined;
    return catalog ? catalog.find((s) => s.key === key) : SITE_BY_KEY.get(key);
}
/**
 * Resolve the agency's stored ask-list (null/invalid → the default starter set), keys only, deduped.
 * Keys are validated against `catalog` (built-in by default; pass the business catalog to allow its
 * custom sites through).
 */
export function credentialSitesOf(stored, catalog = CREDENTIAL_SITES) {
    const valid = new Set(catalog.map((s) => s.key));
    const keys = Array.isArray(stored)
        ? stored.filter((k) => typeof k === 'string' && valid.has(k))
        : DEFAULT_CREDENTIAL_SITE_KEYS;
    return Array.from(new Set(keys.length ? keys : DEFAULT_CREDENTIAL_SITE_KEYS));
}
/**
 * Resolve the agency's REQUIRED site keys (null/invalid → none), kept to the subset that's actually
 * being asked for (`askedKeys`) and deduped. A site can only be required if it's also enabled, so a
 * stale required key that was later un-asked simply drops out.
 */
export function requiredCredentialSitesOf(stored, askedKeys) {
    const asked = new Set(askedKeys);
    const keys = Array.isArray(stored) ? stored.filter((k) => typeof k === 'string' && asked.has(k)) : [];
    return Array.from(new Set(keys));
}
/** Input for creating / updating one personal-vault credential. */
export const credentialInputSchema = z.object({
    /** Catalog key when it maps to a known site, else null for a custom one. */
    site_key: z.string().regex(SITE_KEY_RE).nullable().optional(),
    /** Display label — required (defaults to the catalog label client-side). */
    label: z.string().trim().min(1).max(120),
    /** Optional site/login URL (validated as http(s) on the server via isSafeHttpUrl). */
    url: z.string().trim().max(500).optional().nullable(),
    username: z.string().min(1).max(500),
    password: z.string().min(1).max(500),
    note: z.string().max(2000).optional().nullable(),
});
/** Patch (any subset) for an existing vault credential. Secrets only re-encrypt when present. */
export const credentialPatchSchema = credentialInputSchema.partial();
export const credentialSiteKeysSchema = z.array(z.string().regex(SITE_KEY_RE)).max(40);
//# sourceMappingURL=credentials.js.map